import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import { Button, EmptyState } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { TRIAL_BANNER_HEIGHT } from "app/components/React/TrialBanner/StyledTrialBanner";
import { translate } from "app/filters/i18nFilter";
import { AIAgentCaption } from "app/pages/sales-intelligence/modules/ai-agent/components/AIAgentCaption";
import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import { ActionsContainer } from "app/pages/sales-intelligence/pages/ai-agent/containers/ActionsContainer/ActionsContainer";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

type AIAgentMeetingErrorViewProps = {
    resetErrorBoundary: () => void;
};

export const AIAgentMeetingErrorView = ({ resetErrorBoundary }: AIAgentMeetingErrorViewProps) => {
    const queryClient = useQueryClient();
    const { params } = useCurrentStateAndParams<{ id?: string }>();
    const isTrialBannerVisible = isTrialOrAfterTrial();
    const topOffset = isTrialBannerVisible ? TRIAL_BANNER_HEIGHT : 0;

    const handleRetry = () => {
        void queryClient.resetQueries({ queryKey: aiAgentQueryKeys.generateThread(), exact: true });
        if (params.id) {
            void queryClient.resetQueries({ queryKey: aiAgentQueryKeys.threadById(params.id) });
        }
        resetErrorBoundary();
    };

    const renderCtaButton = () => {
        return (
            <Button
                onClick={handleRetry}
                data-automation="ai-agent-meeting-error-retry"
            >
                {translate("si.common.refresh")}
            </Button>
        );
    };

    const renderImage = () => <SWReactIcons iconName="empty-state-browser" />;

    return (
        <Flex
            flexDirection="column"
            alignItems="center"
            minHeight={isTrialBannerVisible ? `calc(100vh - ${TRIAL_BANNER_HEIGHT}px)` : "100vh"}
            data-automation="ai-agent-meeting-error-view"
        >
            <ActionsContainer
                variant="chatPage"
                topOffset={topOffset}
            />
            <Flex
                flex="1"
                justifyContent="center"
                width="100%"
                background="transparent"
            >
                <EmptyState
                    title={translate("common.error")}
                    SvgImage={renderImage}
                    CtaButtons={renderCtaButton}
                    subtitle={translate("si.common.error.reload")}
                />
            </Flex>
            <AIAgentCaption />
        </Flex>
    );
};
